/**
 * BankSetupView -- the Day-1 onboarding front door (P252).
 *
 * Route: /admin/bank-setup. Admin-only.
 *
 * Two panels:
 *  (a) Branding -- bank display name + accent colour + logo upload, with a
 *      LIVE PREVIEW of the lockup that brands every generated PDF (P251).
 *  (b) Data-source register -- declare every data source (what it feeds,
 *      where it lives, format, owner, canonical-vs-needs-remapping, status):
 *      the operator's "define our data sources" checklist + readiness board.
 */
import { RestoreRecordNote } from "shared/admin/RestoreRecordNote";
import { useTranslation } from "react-i18next";
import { InfoTip } from "shared/components/Explainer";
import { useEffect, useState } from "react";
import { AdminOnlyPanel } from "shared/admin/AdminOnlyPanel";
import { Link } from "react-router-dom";

import { MarsadIcon } from "shared/components/MarsadIcon";
import { useBankProfile, usePatchBankProfile } from "shared/api/hooks/useBankProfile";
import { useAdminSettings } from "shared/api/hooks/useAdminSettings";
import { useConnectionReadiness } from "shared/api/hooks/useConnectionReadiness";
import {
  useCreateDataSource,
  useDataSources,
  useDeleteDataSource,
  useUpdateDataSource,
} from "shared/api/hooks/useDataSources";
import { useMe } from "shared/api/useMe";
import type {
  DataSource,
  DataSourceClass,
  DataSourceInput,
  DataSourceMethod,
  DataSourceStatus,
} from "shared/api/types";
import { Button } from "shared/components/Button";
import { StatusPill } from "shared/components/StatusPill";
import { useToast } from "shared/toast/ToastProvider";

const MAX_LOGO_BYTES = 256 * 1024;
const CLASSES: DataSourceClass[] = ["reference", "mock", "demo", "bank"];
const METHODS: DataSourceMethod[] = ["connector", "csv", "api", "manual"];
const STATUSES: DataSourceStatus[] = ["defined", "wired", "verified"];

function statusTone(s: DataSourceStatus): "neutral" | "amber" | "green" {
  return s === "verified" ? "green" : s === "wired" ? "amber" : "neutral";
}

// ── Branding panel ────────────────────────────────────────────────

function BrandingPanel(): JSX.Element {
  const { t } = useTranslation();
  const profile = useBankProfile();
  const patch = usePatchBankProfile();
  const toast = useToast();

  const [name, setName] = useState("");
  const [accent, setAccent] = useState("#C8A24A");
  const [totalCustomers, setTotalCustomers] = useState("");
  const [jurisdiction, setJurisdiction] = useState("");
  const [logo, setLogo] = useState<string | null>(null);
  const [logoDirty, setLogoDirty] = useState(false);

  useEffect(() => {
    if (profile.data) {
      setName(profile.data.display_name);
      setAccent(profile.data.accent_hex);
      setTotalCustomers(
        profile.data.total_customers === null
          ? ""
          : String(profile.data.total_customers),
      );
      setJurisdiction(profile.data.regime_code ?? "");
      setLogo(profile.data.logo_data_uri);
      setLogoDirty(false);
    }
  }, [profile.data]);

  function onLogoFile(file: File): void {
    if (!["image/png", "image/jpeg"].includes(file.type)) {
      toast.push({ tone: "error", message: "Logo must be PNG or JPEG" });
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      toast.push({ tone: "error", message: "Logo exceeds 256 KB" });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setLogo(typeof reader.result === "string" ? reader.result : null);
      setLogoDirty(true);
    };
    reader.readAsDataURL(file);
  }

  function onSave(): void {
    const body: Parameters<typeof patch.mutate>[0] = {
      display_name: name,
      accent_hex: accent,
      // Blank clears it. An unsupplied total is honest -- breadth then
      // scores on the absolute fallback rather than a made-up
      // denominator.
      total_customers: totalCustomers.trim() === "" ? null : Number(totalCustomers),
      // F2: which regulator is this bank's own. Blank means "do not
      // assume" -- the obligations panel then shows every jurisdiction
      // rather than guessing which one is home.
      regime_code: jurisdiction.trim() === "" ? null : jurisdiction.trim(),
    };
    if (logoDirty) {
      if (logo) body.logo_data_uri = logo;
      else body.clear_logo = true;
    }
    patch.mutate(body, {
      onSuccess: () => {
        setLogoDirty(false);
        toast.push({ tone: "success", message: "Branding saved" });
      },
      onError: (e) => toast.push({ tone: "error", message: e.message }),
    });
  }

  return (
    <section
      data-testid="bank-setup-branding"
      className="rounded-lg border border-paper-2 bg-white p-5 space-y-4"
    >
      <h2 className="font-display text-lg">{t("screens.bankSetupView.branding")}</h2>
      <p className="text-sm text-slate-600">
        {t("screens.bankSetupView.brandsEveryGeneratedPdfDossierBoardSnapshot")}
      </p>

      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block text-sm">
          <span className="text-slate-700">
            {t("screens.bankSetupView.bankDisplayName")}
          </span>
          <input
            dir="auto"
            data-testid="bank-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-1 w-full rounded border border-slate-300 px-2 py-1"
          />
        </label>
        <label className="block text-sm">
          <span className="text-slate-700">
            {t("screens.bankSetupView.accentColour")}
          </span>
          <span className="mt-1 flex items-center gap-2">
            <input
              data-testid="bank-accent-input"
              type="color"
              value={accent}
              onChange={(e) => setAccent(e.target.value)}
              className="h-8 w-12 rounded border border-slate-300"
            />
            <input
              dir="auto"
              value={accent}
              onChange={(e) => setAccent(e.target.value)}
              className="w-28 rounded border border-slate-300 px-2 py-1 font-mono"
            />
          </span>
        </label>
        <label className="block text-sm">
          <span className="text-slate-700">
            {t("screens.bankSetupView.homeJurisdiction")}
          </span>
          <select
            data-testid="bank-jurisdiction-input"
            value={jurisdiction}
            onChange={(e) => setJurisdiction(e.target.value)}
            className="mt-1 w-full rounded border border-slate-300 px-2 py-1"
          >
            <option value="">
              {t("screens.bankSetupView.notSetShowEveryJurisdiction")}
            </option>
            <option value="sama">{t("screens.bankSetupView.saudiArabiaSama")}</option>
            <option value="cbuae">
              {t("screens.bankSetupView.unitedArabEmiratesCbuae")}
            </option>
            <option value="eu">{t("screens.bankSetupView.europeanUnionDora")}</option>
          </select>
          <span className="mt-1 block text-xs text-slate-500">
            {t("screens.bankSetupView.yourPrimaryRegulatorObligationsFromOtherJuri")}
          </span>
        </label>
        <label className="block text-sm">
          <span className="text-slate-700">
            {t("screens.bankSetupView.totalCustomers")}
          </span>
          <input
            data-testid="bank-total-customers-input"
            type="number"
            min={1}
            value={totalCustomers}
            onChange={(e) => setTotalCustomers(e.target.value)}
            className="mt-1 w-full rounded border border-slate-300 px-2 py-1 font-mono"
          />
          <span className="mt-1 block text-xs text-slate-500">
            {t("screens.bankSetupView.theDenominatorIbsBreadthIsScoredAgainst")}
          </span>
        </label>
      </div>

      <div className="flex items-center gap-3">
        <label className="text-sm text-brand-brassDark hover:underline cursor-pointer">
          {t("screens.bankSetupView.uploadLogoPngJpeg256Kb")}
          <input
            data-testid="bank-logo-input"
            type="file"
            accept="image/png,image/jpeg"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onLogoFile(f);
            }}
          />
        </label>
        {logo && (
          <button
            type="button"
            data-testid="bank-logo-clear"
            className="text-sm text-rag-red hover:underline"
            onClick={() => {
              setLogo(null);
              setLogoDirty(true);
            }}
          >
            {t("screens.bankSetupView.clearLogo")}
          </button>
        )}
      </div>

      {/* Live preview of the lockup */}
      <div
        data-testid="bank-preview"
        className="rounded border border-slate-200 bg-paper p-4"
      >
        <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">
          {t("screens.bankSetupView.outputPreview")}
        </div>
        {logo && (
          <img
            src={logo}
            alt="logo preview"
            className="h-10 w-auto max-w-[160px] object-contain mb-1"
          />
        )}
        <div className="font-display text-xl">
          {t("screens.bankSetupView.auditDossier")}
        </div>
        <div className="font-semibold" style={{ color: accent }}>
          {name || t("screens.bankSetupView.marsad")}
        </div>
      </div>

      <Button
        variant="primary"
        data-testid="bank-save"
        disabled={patch.isPending}
        onClick={onSave}
      >
        {patch.isPending
          ? t("screens.bankSetupView.saving")
          : t("screens.bankSetupView.saveBranding")}
      </Button>
    </section>
  );
}

// ── Data-source register panel ────────────────────────────────────

const EMPTY_SOURCE: DataSourceInput = {
  name: "",
  data_class: "bank",
  ingest_method: "csv",
  status: "defined",
  feeds: "",
  location: "",
  source_format: "",
  owner: "",
  is_canonical: true,
  notes: "",
};

function AddSourceForm({ onDone }: { onDone: () => void }): JSX.Element {
  const { t } = useTranslation();
  const create = useCreateDataSource();
  const toast = useToast();
  const [draft, setDraft] = useState<DataSourceInput>(EMPTY_SOURCE);

  function submit(): void {
    if (!draft.name.trim()) {
      toast.push({ tone: "error", message: "Name is required" });
      return;
    }
    create.mutate(draft, {
      onSuccess: () => {
        toast.push({ tone: "success", message: "Data source added" });
        setDraft(EMPTY_SOURCE);
        onDone();
      },
      onError: (e) => toast.push({ tone: "error", message: e.message }),
    });
  }

  return (
    <div
      data-testid="add-source-form"
      className="rounded border border-slate-200 bg-paper p-4 space-y-3"
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <input
          dir="auto"
          data-testid="add-source-name"
          placeholder="Name (e.g. ServiceNow CMDB)"
          value={draft.name}
          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          className="rounded border border-slate-300 px-2 py-1 text-sm"
        />
        <input
          dir="auto"
          placeholder="Feeds (e.g. Services + CIs)"
          value={draft.feeds ?? ""}
          onChange={(e) => setDraft({ ...draft, feeds: e.target.value })}
          className="rounded border border-slate-300 px-2 py-1 text-sm"
        />
        <input
          dir="auto"
          placeholder={t("screens.bankSetupView.locationWhereItLives")}
          value={draft.location ?? ""}
          onChange={(e) => setDraft({ ...draft, location: e.target.value })}
          className="rounded border border-slate-300 px-2 py-1 text-sm"
        />
        <input
          dir="auto"
          placeholder="Format (CSV / REST / OTLP …)"
          value={draft.source_format ?? ""}
          onChange={(e) => setDraft({ ...draft, source_format: e.target.value })}
          className="rounded border border-slate-300 px-2 py-1 text-sm"
        />
        <input
          dir="auto"
          placeholder="Owner (team / contact)"
          value={draft.owner ?? ""}
          onChange={(e) => setDraft({ ...draft, owner: e.target.value })}
          className="rounded border border-slate-300 px-2 py-1 text-sm"
        />
      </div>
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <label>
          {t("screens.bankSetupView.class")}
          {/* F2: "Class" and "Canonical" are the platform admin's own
              vocabulary, met cold while adding the first data source. */}
          <InfoTip termKey="data_source_class" />{" "}
          <select
            value={draft.data_class}
            onChange={(e) =>
              setDraft({ ...draft, data_class: e.target.value as DataSourceClass })
            }
            className="rounded border border-slate-300 px-1 py-1"
          >
            {CLASSES.map((c) => (
              <option key={c}>{c}</option>
            ))}
          </select>
        </label>
        <label>
          {t("screens.bankSetupView.method")}{" "}
          <select
            value={draft.ingest_method}
            onChange={(e) =>
              setDraft({ ...draft, ingest_method: e.target.value as DataSourceMethod })
            }
            className="rounded border border-slate-300 px-1 py-1"
          >
            {METHODS.map((m) => (
              <option key={m}>{m}</option>
            ))}
          </select>
        </label>
        <label>
          {t("screens.bankSetupView.status")}{" "}
          <select
            value={draft.status}
            onChange={(e) =>
              setDraft({ ...draft, status: e.target.value as DataSourceStatus })
            }
            className="rounded border border-slate-300 px-1 py-1"
          >
            {STATUSES.map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-1">
          <input
            type="checkbox"
            checked={draft.is_canonical}
            onChange={(e) => setDraft({ ...draft, is_canonical: e.target.checked })}
          />
          {t("screens.bankSetupView.canonical")}
        </label>
      </div>
      <div className="flex gap-2">
        <Button
          variant="primary"
          data-testid="add-source-submit"
          disabled={create.isPending}
          onClick={submit}
        >
          {t("screens.bankSetupView.addSource")}
        </Button>
        <Button variant="quiet" onClick={onDone}>
          {t("screens.bankSetupView.cancel")}
        </Button>
      </div>
    </div>
  );
}

function SourceRow({ row }: { row: DataSource }): JSX.Element {
  const { t } = useTranslation();
  const update = useUpdateDataSource();
  const del = useDeleteDataSource();
  const toast = useToast();
  /* ROUND 15 F6 -- deleting a data source acted on one press, and a
     second press said "That did not save: data source not found". The
     first press asks; the second acts and says what it did. */
  const [confirming, setConfirming] = useState(false);
  const onDelete = () => {
    if (!confirming) {
      setConfirming(true);
      return;
    }
    del.mutate(row.id, {
      onSuccess: () =>
        toast.push({
          tone: "success",
          message: t("screens.bankSetupView.deleted", { name: row.name }),
        }),
      onError: (e) =>
        toast.push({
          tone: "error",
          message: t("screens.bankSetupView.deleteFailed", { name: row.name }),
          detail: e.message,
        }),
    });
  };

  return (
    <tr data-testid={`source-row-${row.id}`} className="border-t border-slate-100">
      <td className="py-2 pe-3 font-medium">{row.name}</td>
      <td className="py-2 pe-3 text-slate-600">{row.feeds || "—"}</td>
      <td className="py-2 pe-3">
        <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">
          {row.data_class}
        </span>{" "}
        <span className="text-xs text-slate-500">{row.ingest_method}</span>
      </td>
      <td className="py-2 pe-3">
        {row.is_canonical ? (
          <span className="text-xs text-slate-500">
            {t("screens.bankSetupView.canonical2")}
          </span>
        ) : (
          <span className="text-xs text-rag-amber">
            {t("screens.bankSetupView.needsRemapping")}
          </span>
        )}
      </td>
      <td className="py-2 pe-3">
        <select
          aria-label="status"
          value={row.status}
          onChange={(e) =>
            update.mutate(
              { id: row.id, patch: { status: e.target.value as DataSourceStatus } },
              { onError: (err) => toast.push({ tone: "error", message: err.message }) },
            )
          }
          className="rounded border border-slate-300 px-1 py-0.5 text-xs"
        >
          {STATUSES.map((s) => (
            <option key={s}>{s}</option>
          ))}
        </select>
      </td>
      <td className="py-2">
        <StatusPill tone={statusTone(row.status)}>{row.status}</StatusPill>
      </td>
      <td className="py-2 ps-3 text-end">
        <button
          type="button"
          data-testid={`source-delete-${row.id}`}
          data-confirming={confirming}
          className="text-xs text-rag-red hover:underline"
          onClick={onDelete}
          onBlur={() => setConfirming(false)}
        >
          {confirming
            ? t("screens.bankSetupView.deleteConfirm")
            : t("screens.bankSetupView.delete")}
        </button>
      </td>
    </tr>
  );
}

function RegisterPanel(): JSX.Element {
  const { t } = useTranslation();
  const sources = useDataSources();
  const [adding, setAdding] = useState(false);

  return (
    <section
      data-testid="bank-setup-register"
      className="rounded-lg border border-paper-2 bg-white p-5 space-y-4"
    >
      <div className="flex items-center justify-between">
        <h2 className="font-display text-lg">
          {t("screens.bankSetupView.dataSources")}
        </h2>
        <Button
          variant="ghost"
          data-testid="add-source-toggle"
          onClick={() => setAdding((v) => !v)}
        >
          {/* E3 -- the "+" was inside the sentence, so on an RTL line it
              sat at the far end of the words it belongs to. A glyph that
              marks the button is the button's, not the translation's. */}
          {adding ? (
            t("screens.bankSetupView.close")
          ) : (
            <>
              <span aria-hidden="true" className="me-1">
                +
              </span>
              {t("screens.bankSetupView.addSource2")}
            </>
          )}
        </Button>
      </div>
      <p className="text-sm text-slate-600">
        {t("screens.bankSetupView.declareWhereEachKindOfDataComes")}
      </p>

      {adding && <AddSourceForm onDone={() => setAdding(false)} />}

      {sources.isLoading && (
        <p className="text-sm text-slate-500">{t("screens.bankSetupView.loading")}</p>
      )}
      {sources.data && sources.data.length === 0 && !adding && (
        <p data-testid="register-empty" className="text-sm italic text-slate-500">
          {t("screens.bankSetupView.noDataSourcesDefinedYet")}
        </p>
      )}
      {sources.data && sources.data.length > 0 && (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-start text-xs uppercase tracking-wide text-slate-400">
              <th className="pb-1 pe-3">{t("screens.bankSetupView.source")}</th>
              <th className="pb-1 pe-3">{t("screens.bankSetupView.feeds")}</th>
              <th className="pb-1 pe-3">{t("screens.bankSetupView.classMethod")}</th>
              <th className="pb-1 pe-3">{t("screens.bankSetupView.shape")}</th>
              <th className="pb-1 pe-3">{t("screens.bankSetupView.setStatus")}</th>
              <th className="pb-1">{t("screens.bankSetupView.status")}</th>
              <th className="pb-1" />
            </tr>
          </thead>
          <tbody>
            {sources.data.map((row) => (
              <SourceRow key={row.id} row={row} />
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

// ── Page ──────────────────────────────────────────────────────────

const READINESS_TONE: Record<string, string> = {
  verified: "bg-emerald-100 text-emerald-800",
  configured: "bg-slate-100 text-slate-700",
  not_configured: "bg-amber-100 text-amber-800",
  failing: "bg-rose-100 text-rose-800",
};

const READINESS_LABEL: Record<string, string> = {
  verified: "verified",
  configured: "configured",
  not_configured: "credentials missing",
  failing: "failing",
};

/**
 * P382.3 -- connected-systems readiness on the Day-1 page.
 *
 * The credential-encryption warning lives here rather than only inside
 * an expanded connector row: an unset key disables credential entry for
 * the WHOLE deployment, and finding that out from a collapsed row is
 * too late.
 */
function ConnectionsPanel(): JSX.Element {
  const { t } = useTranslation();
  const readiness = useConnectionReadiness();

  if (readiness.isPending) {
    return (
      <section
        data-testid="bank-setup-connections"
        className="rounded border border-paper-2 bg-white p-4"
      >
        <p className="text-sm text-slate-500">
          {t("screens.bankSetupView.loadingConnectedSystems")}
        </p>
      </section>
    );
  }
  if (readiness.isError || !readiness.data) {
    return (
      <section
        data-testid="bank-setup-connections"
        className="rounded border border-paper-2 bg-white p-4"
      >
        <p className="text-sm text-slate-500">
          {t("screens.bankSetupView.connectedSystemsCouldNotBeLoaded")}
        </p>
      </section>
    );
  }

  const data = readiness.data;
  // Defensive: a partial payload must degrade, not throw. Same guard
  // class as P76 -- a panel that crashes takes the whole Day-1 page
  // with it, which is the worst possible moment for it to happen.
  const all = Array.isArray(data.connectors) ? data.connectors : [];
  const enabled = all.filter((c) => c.enabled);

  return (
    <section
      data-testid="bank-setup-connections"
      className="rounded border border-paper-2 bg-white p-4 shadow-card"
    >
      <h2 className="font-display text-lg">
        {t("screens.bankSetupView.connectedSystems")}
      </h2>
      <p className="mb-3 text-sm text-slate-600">
        {data.verified_count} {t("screens.bankSetupView.of")} {data.enabled_count}{" "}
        {t("screens.bankSetupView.enabledConnector")}
        {data.enabled_count === 1
          ? t("screens.bankSetupView.has")
          : t("screens.bankSetupView.sHave")}{" "}
        {t("screens.bankSetupView.deliveredData")}
      </p>

      {!data.credential_encryption_configured && (
        <div
          data-testid="bank-setup-no-encryption-key"
          className="mb-3 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
        >
          <p className="font-semibold">
            {t("screens.bankSetupView.credentialsCannotBeStoredYet")}
          </p>
          <p className="mt-1">
            {t("screens.bankSetupView.marsadCredentialEncryptionKeyIsNotSet")}
          </p>
          <code className="mt-1 block whitespace-pre-wrap break-all rounded bg-white/70 px-2 py-1 font-mono text-[10px]">
            {t("screens.bankSetupView.dockerComposeExecBackendPythonCQuot")}
          </code>
        </div>
      )}

      {/* D5 -- this link used to appear only while NO connector was
          enabled, so connecting the SECOND system had no route. */}
      <p className="text-sm text-slate-600">
        {enabled.length === 0 ? t("screens.bankSetupView.noConnectorIsEnabledYet") : ""}
        <Link
          to="/admin/connections/setup"
          data-testid="bank-setup-connect-a-system"
          className="text-brand-brassDark hover:underline"
        >
          {t("screens.bankSetupView.connectASystem")}
        </Link>{" "}
        {t("screens.bankSetupView.walksYouThroughCredentialsAConnectionTest")}
      </p>

      {enabled.length === 0 ? (
        <p data-testid="bank-setup-no-connectors" className="sr-only">
          {t("screens.bankSetupView.noConnectorIsEnabledYet")}
        </p>
      ) : (
        <ul className="space-y-1">
          {enabled.map((c) => (
            <li
              key={c.connector_id}
              data-testid={`bank-setup-connection-${c.kind}`}
              className="flex items-center justify-between rounded border border-paper-2 px-2 py-1 text-xs"
            >
              <span className="font-medium text-slate-700">{c.label}</span>
              <span className="flex items-center gap-2">
                {c.missing_credentials.length > 0 && (
                  <span className="text-[10px] text-slate-500">
                    {t("screens.bankSetupView.needs")}{" "}
                    {c.missing_credentials.join(", ")}
                  </span>
                )}
                <span
                  className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${
                    READINESS_TONE[c.readiness] ?? "bg-slate-100 text-slate-700"
                  }`}
                >
                  {READINESS_LABEL[c.readiness] ?? c.readiness}
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * Round 12 G1 -- where this deployment runs, said on a screen.
 *
 * The Azure work is careful and honest and is entirely invisible. The
 * deployment document is 285 lines naming one region, HSTS with its
 * two-year commitment spelled out, a real certificate, published proof
 * keys, single tenant by construction, and a closing section headed
 * "Known limits, stated rather than discovered" that admits the restore
 * has never been run. None of it reached a surface anybody reads, on a
 * product sold on sovereignty.
 *
 * NOT STATED is a real answer and the default one. A screen that named
 * a region the operator had not told it would be the worst possible
 * place for this product to be confidently wrong -- residency is a
 * claim a bank makes to its regulator, and the product repeats what it
 * was told rather than inferring anything.
 */
function WhereItRunsPanel(): JSX.Element {
  const { t } = useTranslation();
  const settings = useAdminSettings();
  const value = (key: string): string | null => {
    const row = (settings.data?.env ?? []).find((e) => e.key === key);
    const v = row?.value;
    return v && v.trim() ? v.trim() : null;
  };
  const region = value("MARSAD_DEPLOYMENT_REGION");
  const hosting = value("MARSAD_DEPLOYMENT_HOSTING");
  // Round 16 K12 -- backup and restore beside region and hosting, as the
  // dossier cover and the examiner already print them.
  const backup = value("MARSAD_DEPLOYMENT_BACKUP");

  return (
    <section
      data-testid="bank-setup-where-it-runs"
      data-stated={region ? "true" : "false"}
      className="rounded-xl border border-paper-2 bg-white p-4 shadow-card"
    >
      <h2 className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-slate-400">
        {t("screens.bankSetupView.whereItRuns")}
      </h2>
      {region ? (
        <>
          <p
            data-testid="bank-setup-region"
            className="mt-1 text-sm font-medium text-slate-900"
          >
            {region}
          </p>
          {hosting && (
            <p
              data-testid="bank-setup-hosting"
              className="mt-0.5 text-xs text-slate-600"
            >
              {hosting}
            </p>
          )}
          <p className="mt-2 max-w-2xl text-xs text-slate-500">
            {t("screens.bankSetupView.whereItRunsSource")}
          </p>
        </>
      ) : (
        <p
          data-testid="bank-setup-region-unstated"
          className="mt-1 max-w-2xl text-sm text-slate-600"
        >
          {t("screens.bankSetupView.whereItRunsUnstated")}
        </p>
      )}
      <p data-testid="bank-setup-backup" className="mt-2 text-xs text-slate-600">
        {t("screens.restoreRecord.backup", {
          line: backup ?? t("screens.restoreRecord.notStated"),
        })}
      </p>
      <div className="mt-1">
        <RestoreRecordNote restore={settings.data?.restore} />
      </div>
    </section>
  );
}

export function BankSetupView(): JSX.Element {
  const { t } = useTranslation();
  const me = useMe();
  const isAdmin = me.data?.is_admin ?? false;

  if (!isAdmin) {
    return <AdminOnlyPanel surface="Bank setup" />;
  }

  return (
    <article data-testid="bank-setup" className="max-w-4xl space-y-6">
      <header>
        <h1 className="font-display text-2xl" data-testid="bank-setup-heading">
          <MarsadIcon
            name="foundation"
            size={20}
            className="me-2 inline-block align-[-0.1em] text-brand-brassDark"
          />
          {t("screens.bankSetupView.bankSetup")}
        </h1>
        <p className="text-slate-600">
          {t("screens.bankSetupView.day1OnboardingTheBankSIdentity")}
        </p>
      </header>
      <WhereItRunsPanel />
      <BrandingPanel />
      <ConnectionsPanel />
      <RegisterPanel />
    </article>
  );
}
